import { ApiError } from '@hq/api-sdk';
import { IcpFooter } from '@hq/ui-kit';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { readOrgBrand, saveOrgBrandFromLogin, type OrgBrand } from '../org-brand';
import { appTitle, hq, useAuthStore } from '../session';

interface LoginValues {
  account: string;
  password: string;
}

/** 副标题：品牌端也只显短名，全称悬浮展示，保证各端面板结构/高度一致。 */
const SUBTITLE = '供应商端 · 订单协同 / 履约 / 对账';

/** 供应商端登录：账号密码（门户 SUPPLIER）。 */
export default function Login() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((state) => state.setSession);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 上次登录落盘的机构品牌（localStorage，退出登录/关闭浏览器都不清）。 */
  const [brand, setBrand] = useState<OrgBrand | null>(readOrgBrand);

  const goNext = () => {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== '/login' ? from : '/dashboard', { replace: true });
  };

  const onFinish = async (values: LoginValues) => {
    setLoading(true);
    setError(null);
    try {
      const result = await hq.auth.login({
        account: values.account,
        password: values.password,
        // 同账号同应用单处在线：供应商端登录会顶掉本端此前的登录（其它端不受影响）
        app: 'web-supplier'
      });
      setSession(result.token, {
        userId: String(result.profile.userId),
        orgId: String(result.profile.orgId),
        orgName: result.profile.orgName ?? null,
        deptId: String(result.profile.deptId),
        deptName: result.profile.deptName ?? null,
        userCode: result.profile.userCode ?? null,
        name: result.profile.realName ?? result.profile.userCode,
        level: result.profile.level ?? 'L4',
        perms: result.profile.perms ?? [],
        avatarUrl: result.profile.avatarUrl ?? null,
        mobile: result.profile.mobile ?? null,
        email: result.profile.email ?? null
      });
      message.success(`欢迎，${result.profile.realName ?? result.profile.userCode}`);
      void saveOrgBrandFromLogin(hq, {
        orgId: String(result.profile.orgId),
        orgName: result.profile.orgName ?? null
      })
        .then((saved) => {
          if (saved) setBrand(saved);
        })
        .catch(() => undefined);
      goNext();
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(135deg, var(--hq-color-primary-active, #6F0863) 0%, var(--hq-color-primary, #88157A) 55%, var(--hq-color-primary-hover, #AF4FA3) 100%)'
      }}
    >
      {/* 装饰层：柔光 + 点阵纹理，纯 CSS 实现（不依赖图片资源） */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: [
            'radial-gradient(55% 45% at 15% 12%, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0) 70%)',
            'radial-gradient(50% 50% at 88% 85%, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0) 70%)',
            'radial-gradient(rgba(255, 255, 255, 0.10) 1px, transparent 1px)'
          ].join(', '),
          backgroundSize: 'auto, auto, 24px 24px'
        }}
      />
      <Card
        style={{ width: 380, maxWidth: '100%', border: 'none', borderRadius: 14, position: 'relative', boxShadow: '0 24px 64px rgba(46, 4, 41, 0.45)' }}
        styles={{ body: { padding: '28px 28px 24px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div
            style={{
              width: 72,
              height: 72,
              margin: '0 auto',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.14)',
              boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.22)'
            }}
          >
            <img
              src={brand?.logoUrl || '/logo.png'}
              alt="logo"
              style={{ width: 44, height: 44, objectFit: 'contain' }}
            />
          </div>
          <Typography.Title level={4} style={{ margin: '12px 0 2px' }}>
            {brand?.orgShortName || brand?.orgName || appTitle}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }} title={brand?.orgName ?? undefined}>
            {SUBTITLE}
          </Typography.Paragraph>
        </div>

        {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

        <Form<LoginValues> layout="vertical" onFinish={onFinish} autoComplete="off" style={{ marginBottom: -8 }}>
          <Form.Item name="account" label="账号" rules={[{ required: true, message: '请输入账号' }]}>
            <Input size="large" prefix={<UserOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />} placeholder="工号 / 手机号" autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password size="large" prefix={<LockOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />} placeholder="密码" autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block size="large" loading={loading} style={{ height: 40, marginTop: 4 }}>
            登录
          </Button>
        </Form>

        <Typography.Paragraph
          type="secondary"
          style={{ textAlign: 'center', fontSize: 12, margin: '16px 0 0' }}
        >
          智慧物资管理平台
        </Typography.Paragraph>
        <IcpFooter />
      </Card>
    </div>
  );
}
